import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Key, X, CheckCircle, XCircle, User, Buildings, ShieldCheck, UserPlus, Clock, Trash, Database, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = [
  { v: "fiscal_municipal", l: "Fiscal Municipal" },
  { v: "admin", l: "Administrador Geral" },
];

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState("pending"); // pending | register | active
  const [pending, setPending] = useState([]);
  const [active, setActive] = useState([]);
  const [munis, setMunis] = useState([]);
  const [creds, setCreds] = useState(null);

  const loadPending = () =>
    api.get("/admin/users?pending=true").then((r) => setPending(r.data.items));
  const loadActive = () =>
    api.get("/admin/users?pending=false").then((r) => setActive(r.data.items));

  useEffect(() => {
    api.get("/municipalities").then((r) => setMunis(r.data));
    loadPending();
    loadActive();
  }, []);

  if (currentUser && currentUser.role !== "admin") return <Navigate to="/dashboard" replace />;

  const authorize = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/authorize`);
      toast.success(`${u.name} autorizado a acessar o sistema`);
      loadPending(); loadActive();
    } catch { toast.error("Erro ao autorizar"); }
  };

  const revoke = async (u) => {
    if (!window.confirm(`Revogar acesso de ${u.email}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/revoke`);
      toast.success("Acesso revogado");
      loadPending(); loadActive();
    } catch { toast.error("Erro ao revogar"); }
  };

  const reject = async (u) => {
    if (!window.confirm(`Recusar e excluir cadastro de ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Cadastro recusado");
      loadPending();
    } catch { toast.error("Erro ao recusar"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="uppercase-label">Controle de acessos</div>
        <h1 className="font-heading text-4xl tracking-tight mt-2">Cadastro de Fiscais</h1>
        <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl">
          Cadastre novos fiscais por município. Cadastros realizados pelos próprios fiscais
          via tela de registro ficam aguardando autorização do administrador geral antes de acessar o sistema.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-[#334155]">
        <TabBtn active={tab === "pending"} onClick={() => setTab("pending")}
                icon={Clock} testid="tab-pending"
                label={`Aprovações Pendentes${pending.length > 0 ? ` (${pending.length})` : ""}`}
                badge={pending.length > 0} />
        <TabBtn active={tab === "register"} onClick={() => setTab("register")}
                icon={UserPlus} testid="tab-register" label="Cadastrar Fiscal" />
        <TabBtn active={tab === "active"} onClick={() => setTab("active")}
                icon={ShieldCheck} testid="tab-active"
                label={`Fiscais Autorizados (${active.length})`} />
        <TabBtn active={tab === "manutencao"} onClick={() => setTab("manutencao")}
                icon={Database} testid="tab-manutencao" label="Manutenção de Dados" />
      </div>

      {tab === "pending" && (
        <PendingList items={pending} onAuthorize={authorize} onReject={reject} />
      )}

      {tab === "register" && (
        <RegisterForm munis={munis} onCreated={(c) => { setCreds(c); loadActive(); }} />
      )}

      {tab === "active" && (
        <ActiveList items={active} onRevoke={revoke}
                    onResetPassword={async (u) => {
                      if (!window.confirm(`Resetar senha de ${u.email}?`)) return;
                      try {
                        const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
                        setCreds({ email: u.email, password: data.new_password, title: "Nova senha gerada" });
                      } catch { toast.error("Erro ao resetar"); }
                    }} />
      )}

      {tab === "manutencao" && <ManutencaoPanel />}

      {creds && <CredsModal data={creds} onClose={() => setCreds(null)} />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid, badge }) {
  return (
    <button data-testid={testid} onClick={onClick}
            className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${
              active ? "border-[#F59E0B] text-[#F59E0B]" : "border-transparent text-[#94A3B8] hover:text-[#E9EEF5]"
            }`}>
      <Icon size={16} weight={active ? "duotone" : "regular"} />
      {label}
      {badge && <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" />}
    </button>
  );
}

function PendingList({ items, onAuthorize, onReject }) {
  if (items.length === 0) {
    return (
      <div className="tce-card p-10 text-center">
        <ShieldCheck size={36} weight="duotone" className="text-[#F59E0B] mx-auto mb-3" />
        <div className="font-heading text-lg">Sem solicitações pendentes</div>
        <p className="text-sm text-[#94A3B8] mt-2">
          Todos os cadastros recentes já foram processados.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {items.map((u, i) => (
        <div key={u.id} data-testid={`pending-row-${i}`} className="tce-card p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-[#F59E0B]/15 flex items-center justify-center text-[#F59E0B]">
            <Clock size={18} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-[#E9EEF5]">{u.name}</div>
            <div className="text-xs text-[#94A3B8]">{u.email}</div>
            {u.municipio_nome && (
              <div className="text-xs text-[#F59E0B] mt-1 flex items-center gap-1">
                <Buildings size={12} weight="duotone" /> {u.municipio_nome}
              </div>
            )}
          </div>
          <div className="text-xs text-[#94A3B8]">
            Solicitado em {new Date(u.created_at).toLocaleDateString("pt-BR")}
          </div>
          <button data-testid={`reject-${i}`} onClick={() => onReject(u)}
                  className="px-4 py-2 rounded-md border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 text-sm font-medium flex items-center gap-1.5">
            <XCircle size={14} /> Recusar
          </button>
          <button data-testid={`authorize-${i}`} onClick={() => onAuthorize(u)}
                  className="px-4 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] text-sm font-medium flex items-center gap-1.5">
            <CheckCircle size={14} weight="fill" /> Autorizar acesso
          </button>
        </div>
      ))}
    </div>
  );
}

function RegisterForm({ munis, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "fiscal_municipal", municipio_id: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const muniName = munis.find((m) => m.ibge_code === form.municipio_id)?.name;
      const { data } = await api.post("/admin/users", { ...form, municipio_nome: muniName, active: true });
      toast.success("Fiscal cadastrado e já autorizado");
      onCreated(data.password_generated ? { email: data.email, password: data.password_generated, title: "Senha gerada" } : null);
      setForm({ email: "", name: "", password: "", role: "fiscal_municipal", municipio_id: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar");
    } finally { setSaving(false); }
  };

  return (
    <div className="tce-card p-7 max-w-2xl">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-md bg-[#F59E0B]/15 flex items-center justify-center text-[#F59E0B]">
          <UserPlus size={20} weight="duotone" />
        </div>
        <div>
          <div className="uppercase-label">Cadastro direto</div>
          <div className="font-heading text-xl mt-1">Novo fiscal</div>
        </div>
      </div>
      <p className="text-sm text-[#94A3B8] mb-6">
        Fiscais cadastrados aqui pelo administrador são <span className="text-[#10B981]">automaticamente autorizados</span>.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="uppercase-label block mb-2">Email funcional</label>
          <input data-testid="register-email" type="email" required value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })}
                 placeholder="fiscal.nome@municipio.ce.gov.br"
                 className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
        </div>
        <div>
          <label className="uppercase-label block mb-2">Nome completo</label>
          <input data-testid="register-name" required value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })}
                 className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="uppercase-label block mb-2">Papel</label>
            <select data-testid="register-role" value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
              {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          <div>
            <label className="uppercase-label block mb-2">Prefeitura</label>
            <select data-testid="register-muni" value={form.municipio_id}
                    onChange={(e) => setForm({ ...form, municipio_id: e.target.value })}
                    disabled={form.role === "admin"} required={form.role === "fiscal_municipal"}
                    className="tce-input w-full px-3 py-2.5 rounded-md text-sm disabled:opacity-60">
              <option value="">— Selecione —</option>
              {munis.map((m) => <option key={m.ibge_code} value={m.ibge_code}>{m.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="uppercase-label block mb-2">Senha (opcional — gera aleatória se vazia)</label>
          <input data-testid="register-password" type="text" value={form.password}
                 onChange={(e) => setForm({ ...form, password: e.target.value })}
                 placeholder="••••••••"
                 className="tce-input w-full px-3 py-2.5 rounded-md text-sm font-mono" />
        </div>
        <button data-testid="register-submit" disabled={saving}
                className="w-full px-4 py-3 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium disabled:opacity-50">
          {saving ? "Cadastrando…" : "Cadastrar fiscal"}
        </button>
      </form>
    </div>
  );
}

function ActiveList({ items, onRevoke, onResetPassword }) {
  return (
    <div className="tce-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1E293B]">
            <tr className="text-left">
              <th className="px-5 py-4 uppercase-label">Fiscal</th>
              <th className="px-5 py-4 uppercase-label">Prefeitura</th>
              <th className="px-5 py-4 uppercase-label">Papel</th>
              <th className="px-5 py-4 uppercase-label text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-[#94A3B8]">Nenhum fiscal autorizado.</td></tr>
            ) : items.map((u, i) => (
              <tr key={u.id} data-testid={`active-row-${i}`} className="border-t border-[#334155] hover:bg-[#1E293B]/60">
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#1E293B] flex items-center justify-center text-[#F59E0B]">
                      <User size={16} weight="duotone" />
                    </div>
                    <div>
                      <div className="text-[#E9EEF5]">{u.name}</div>
                      <div className="text-xs text-[#94A3B8]">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4 text-[#94A3B8]">
                  {u.municipio_nome || <span className="text-xs italic text-[#6B7280]">—</span>}
                </td>
                <td className="px-5 py-4 text-xs">
                  <span className={`px-2 py-1 rounded-full ${u.role === "admin" ? "bg-[#F59E0B]/15 text-[#F59E0B]" : "bg-[#1E293B] text-[#94A3B8]"}`}>
                    {u.role === "admin" ? "Administrador Geral" : "Fiscal Municipal"}
                  </span>
                </td>
                <td className="px-5 py-4 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button data-testid={`reset-${i}`} onClick={() => onResetPassword(u)}
                            title="Resetar senha"
                            className="p-2 rounded hover:bg-[#1E293B] text-[#94A3B8] hover:text-[#F59E0B]">
                      <Key size={15} />
                    </button>
                    {u.role !== "admin" && (
                      <button data-testid={`revoke-${i}`} onClick={() => onRevoke(u)}
                              title="Revogar acesso"
                              className="p-2 rounded hover:bg-[#EF4444]/10 text-[#94A3B8] hover:text-[#EF4444]">
                        <Trash size={15} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CredsModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tce-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-[#334155] flex items-center gap-2">
          <Key size={20} weight="duotone" className="text-[#F59E0B]" />
          <div className="font-heading text-lg">{data.title}</div>
          <button onClick={onClose} className="ml-auto text-[#94A3B8] hover:text-[#F59E0B]"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-[#94A3B8]">
            Anote ou copie a senha agora — ela não será exibida novamente.
          </p>
          <div className="rounded-md bg-[#0F172A] border border-[#F59E0B]/40 p-4 space-y-2">
            <div>
              <div className="uppercase-label">Email</div>
              <div className="font-mono text-[#E9EEF5] text-sm mt-1">{data.email}</div>
            </div>
            <div>
              <div className="uppercase-label">Senha</div>
              <div className="font-mono text-[#F59E0B] text-base mt-1 select-all">{data.password}</div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => {
              navigator.clipboard.writeText(`${data.email} / ${data.password}`);
              toast.success("Credenciais copiadas");
            }} className="px-4 py-2 rounded-md border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10 text-sm">
              Copiar
            </button>
            <button onClick={onClose} className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManutencaoPanel() {
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);

  const refresh = async () => {
    try {
      const [s, st] = await Promise.all([
        api.get("/admin/data-summary"),
        api.get("/admin/migrations/status"),
      ]);
      setSummary(s.data);
      setStatus(st.data);
    } catch {
      toast.error("Erro ao carregar dados");
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, []);

  const trigger = async (kind) => {
    const label = kind === "modalidades" ? "reclassificação de modalidades (Dispensa/Inexigibilidade)" : "atualização de fornecedores (CNPJ real)";
    if (!window.confirm(`Disparar ${label}?\n\nA migração roda em background e pode levar até 15 min. Não interrompe o uso do sistema.`)) return;
    setBusy(kind);
    try {
      const { data } = await api.post(`/admin/migrate-${kind}`);
      toast.success(`Migração iniciada por ${data.started_by}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao iniciar migração");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="tce-card p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="uppercase-label">Estado dos dados</div>
            <div className="font-heading text-xl mt-1">Resumo do banco</div>
          </div>
          <button onClick={refresh} data-testid="refresh-summary"
                  className="p-2 rounded-md text-[#94A3B8] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10">
            <ArrowsClockwise size={16} weight="bold" />
          </button>
        </div>
        {summary ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <SummaryCard label="Total de contratos" value={summary.total_contratos.toLocaleString("pt-BR")} accent />
            <SummaryCard label="Com CNPJ fornecedor" value={summary.tce_sim_com_cnpj.toLocaleString("pt-BR")} />
            <SummaryCard label="Sem CNPJ (pendentes)" value={summary.tce_sim_sem_cnpj.toLocaleString("pt-BR")} />
            {Object.entries(summary.por_modalidade).map(([k, v]) => (
              <SummaryCard key={k} label={k} value={v.toLocaleString("pt-BR")} small />
            ))}
          </div>
        ) : <div className="text-[#94A3B8] text-sm">Carregando…</div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <MigrationCard
          kind="modalidades"
          title="Reclassificar Modalidades"
          description="Cruza /sim/contratos com /sim/processos_administrativos_contratacoes para identificar Dispensa de Licitação, Inexigibilidade, Convite, etc."
          duration="~14 min para 282k contratos"
          state={status?.modalidades}
          busy={busy === "modalidades"}
          onTrigger={() => trigger("modalidades")}
        />
        <MigrationCard
          kind="fornecedores"
          title="Atualizar Fornecedores"
          description="Cruza /sim/contratos com /sim/contratados para popular o CNPJ e razão social do fornecedor real. Move o fiscal do TCE para campo dedicado."
          duration="~4 min para contratos pendentes"
          state={status?.fornecedores}
          busy={busy === "fornecedores"}
          onTrigger={() => trigger("fornecedores")}
        />
      </div>

      <div className="text-xs text-[#94A3B8] italic">
        ⚠️ As migrações são <strong>idempotentes</strong> — podem ser disparadas múltiplas vezes sem duplicar dados. Após reclassificação, atualize o painel para ver os novos contadores.
      </div>

      <SyncPncpItensCard />

      <SyncComprasnetCard />
    </div>
  );
}

function SyncPncpItensCard() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    data_inicio: new Date(Date.now() - 30 * 86400 * 1000).toISOString().slice(0, 10),
    data_fim: new Date().toISOString().slice(0, 10),
    uf: "CE",
  });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/sync-pncp-itens/status");
      setStatus(data);
    } catch { /* silently */ }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, []);

  const dispatch = async () => {
    if (!window.confirm(`Iniciar indexação PNCP de ${form.data_inicio} a ${form.data_fim} (UF=${form.uf})?\n\nRoda em background. Pode levar 5-30 min dependendo do período.`)) return;
    setBusy(true);
    try {
      await api.post("/admin/sync-pncp-itens", form);
      toast.success("Sync de itens PNCP iniciado");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao iniciar sync");
    } finally {
      setBusy(false);
    }
  };

  const isRunning = status?.status === "running";
  const isCompleted = status?.status === "completed";
  const isFailed = status?.status === "failed";

  return (
    <div className="tce-card p-6" data-testid="sync-pncp-itens-card">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="uppercase-label">Indexação · PNCP</div>
          <div className="font-heading text-lg mt-1">Banco indexado de itens PNCP</div>
          <p className="text-sm text-[#94A3B8] mt-1 max-w-2xl">
            Sincroniza itens detalhados de contratos PNCP para o banco local. Permite que a pesquisa de preços encontre itens específicos (ex: &quot;vassoura de pelo&quot;) instantaneamente, contornando a limitação de busca por texto da API PNCP.
          </p>
        </div>
        {isRunning && (
          <span className="text-xs px-2.5 py-1 rounded-full bg-[#F59E0B]/15 text-[#F59E0B] flex items-center gap-1.5 shrink-0">
            <span className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse"></span>
            Sincronizando
          </span>
        )}
        {isCompleted && <span className="text-xs px-2.5 py-1 rounded-full bg-[#10B981]/15 text-[#10B981] shrink-0">Concluído</span>}
        {isFailed && <span className="text-xs px-2.5 py-1 rounded-full bg-[#EF4444]/15 text-[#EF4444] shrink-0">Falhou</span>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
        <SummaryCard label="Total indexado" value={(status?.total_indexados || 0).toLocaleString("pt-BR")} accent />
        <SummaryCard label="Páginas processadas" value={`${status?.progress_page || 0}/${status?.total_paginas || 0}`} small />
        <SummaryCard label="Contratos CE escaneados" value={(status?.contracts_ce || 0).toLocaleString("pt-BR")} small />
        <SummaryCard label="Itens inseridos nessa execução" value={(status?.items_inserted || 0).toLocaleString("pt-BR")} small />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <label className="uppercase-label block mb-2">De</label>
          <input data-testid="sync-di" type="date" value={form.data_inicio}
                 onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
                 disabled={isRunning}
                 className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
        </div>
        <div>
          <label className="uppercase-label block mb-2">Até</label>
          <input data-testid="sync-df" type="date" value={form.data_fim}
                 onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
                 disabled={isRunning}
                 className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
        </div>
        <div>
          <label className="uppercase-label block mb-2">UF</label>
          <input data-testid="sync-uf" value={form.uf} maxLength={2}
                 onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })}
                 disabled={isRunning}
                 className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
        </div>
        <button
          data-testid="trigger-sync-pncp"
          onClick={dispatch}
          disabled={busy || isRunning}
          className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Database size={14} weight="duotone" />
          {isRunning ? "Em execução…" : busy ? "Iniciando…" : "Iniciar sync"}
        </button>
      </div>

      {status?.started_at && (
        <div className="text-xs text-[#94A3B8] mt-3 space-y-0.5">
          <div>Iniciado por: <span className="text-[#E9EEF5]">{status.started_by || "—"}</span> · Período: {status.date_start} a {status.date_end} · UF: {status.uf}</div>
          <div>Início: {new Date(status.started_at).toLocaleString("pt-BR")} {status.finished_at && `· Fim: ${new Date(status.finished_at).toLocaleString("pt-BR")}`}</div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent, small }) {
  return (
    <div className={`p-4 rounded-md border border-[#334155] bg-[#0E141C]/40 ${small ? "" : ""}`}>
      <div className="uppercase-label !text-[10px]">{label}</div>
      <div className={`font-mono ${small ? "text-lg" : "text-2xl"} mt-1 ${accent ? "gold-text" : "text-[#E9EEF5]"}`}>{value}</div>
    </div>
  );
}

function MigrationCard({ kind, title, description, duration, state, busy, onTrigger }) {
  const isRunning = state?.running;
  const result = state?.result;
  return (
    <div className="tce-card p-6 flex flex-col" data-testid={`migration-card-${kind}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="uppercase-label">Migração</div>
          <div className="font-heading text-lg mt-1">{title}</div>
        </div>
        {isRunning ? (
          <span className="text-xs px-2.5 py-1 rounded-full bg-[#F59E0B]/15 text-[#F59E0B] flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse"></span>
            Em execução
          </span>
        ) : result === "ok" ? (
          <span className="text-xs px-2.5 py-1 rounded-full bg-[#10B981]/15 text-[#10B981]">Concluída</span>
        ) : result ? (
          <span className="text-xs px-2.5 py-1 rounded-full bg-[#EF4444]/15 text-[#EF4444]" title={result}>Falhou</span>
        ) : null}
      </div>
      <p className="text-sm text-[#94A3B8] mb-3">{description}</p>
      <div className="text-xs text-[#94A3B8] mb-4">Duração estimada: <span className="text-[#FCD34D]">{duration}</span></div>
      {state?.started_at && (
        <div className="text-xs text-[#94A3B8] mb-3 space-y-0.5">
          <div>Iniciada por: <span className="text-[#E9EEF5]">{state.started_by}</span></div>
          <div>Início: {new Date(state.started_at).toLocaleString("pt-BR")}</div>
          {state.finished_at && <div>Fim: {new Date(state.finished_at).toLocaleString("pt-BR")}</div>}
        </div>
      )}
      {state?.log_tail && isRunning && (
        <pre className="text-[10px] text-[#94A3B8] bg-black/30 rounded p-2 mb-3 max-h-32 overflow-y-auto font-mono">
{state.log_tail}
        </pre>
      )}
      <button
        data-testid={`trigger-${kind}`}
        onClick={onTrigger}
        disabled={busy || isRunning}
        className="mt-auto px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <Database size={14} weight="duotone" />
        {isRunning ? "Em execução…" : busy ? "Iniciando…" : "Executar migração"}
      </button>
    </div>
  );
}

// Classes CATMAT mais comuns para sync inicial
const CLASSES_CATMAT_SUGERIDAS = [
  { codigo: 7920, nome: "Limpeza e conservação" },
  { codigo: 7510, nome: "Material de escritório" },
  { codigo: 8540, nome: "Higiene pessoal e cosméticos" },
  { codigo: 6505, nome: "Medicamentos" },
  { codigo: 6515, nome: "Material médico-hospitalar" },
  { codigo: 8910, nome: "Gêneros alimentícios" },
  { codigo: 7110, nome: "Mobiliário para escritório" },
  { codigo: 7045, nome: "Suprimentos informática" },
  { codigo: 8455, nome: "Vestuário e uniformes" },
  { codigo: 5680, nome: "Materiais de construção" },
];

function SyncComprasnetCard() {
  const [status, setStatus] = useState(null);
  const [classesSelecionadas, setClassesSelecionadas] = useState([7920]);
  const [form, setForm] = useState({
    uf: "CE", data_inicio: "", data_fim: "", max_catmats_por_classe: 50,
  });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/sync-comprasnet-itens/status");
      setStatus(data);
    } catch { /* silently */ }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, []);

  const toggleClasse = (codigo) => {
    setClassesSelecionadas((prev) =>
      prev.includes(codigo) ? prev.filter((c) => c !== codigo) : [...prev, codigo]
    );
  };

  const dispatch = async () => {
    if (classesSelecionadas.length === 0) { toast.error("Selecione ao menos 1 classe"); return; }
    if (!window.confirm(`Iniciar sync Comprasnet para ${classesSelecionadas.length} classe(s) na UF ${form.uf}?\n\nIsso pode levar 5-30min dependendo da quantidade de classes e CATMATs.`)) return;
    setBusy(true);
    try {
      await api.post("/admin/sync-comprasnet-itens", {
        classes: classesSelecionadas,
        uf: form.uf,
        data_inicio: form.data_inicio || null,
        data_fim: form.data_fim || null,
        max_catmats_por_classe: form.max_catmats_por_classe,
      });
      toast.success("Sync Comprasnet iniciado em background");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao iniciar sync");
    } finally {
      setBusy(false);
    }
  };

  const isRunning = status?.status === "running";
  const isCompleted = status?.status === "completed";

  return (
    <div className="tce-card p-6" data-testid="sync-comprasnet-card">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="uppercase-label">Indexação · Comprasnet (Federal)</div>
          <div className="font-heading text-lg mt-1">Banco indexado · Comprasnet / Dados Abertos</div>
          <p className="text-sm text-[#94A3B8] mt-1 max-w-2xl">
            Sincroniza preços históricos do Comprasnet (Pregões federais, ARPs, compras diretas) por classe CATMAT.
            Cobertura nacional — fonte oficial para pesquisa de preços conforme IN SEGES/ME nº 65/2021 Art. 5º, II.
          </p>
        </div>
        {isRunning && (
          <span className="text-xs px-2.5 py-1 rounded-full bg-[#F59E0B]/15 text-[#F59E0B] flex items-center gap-1.5 shrink-0">
            <span className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse"></span>
            Sincronizando
          </span>
        )}
        {isCompleted && <span className="text-xs px-2.5 py-1 rounded-full bg-[#10B981]/15 text-[#10B981] shrink-0">Concluído</span>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
        <SummaryCard label="Total Comprasnet" value={(status?.total_indexados_comprasnet || 0).toLocaleString("pt-BR")} accent />
        <SummaryCard label="Classes processadas" value={`${status?.classes_processadas || 0}/${status?.total_classes || classesSelecionadas.length}`} small />
        <SummaryCard label="CATMATs descobertos" value={(status?.catmats_descobertos || 0).toLocaleString("pt-BR")} small />
        <SummaryCard label="Itens inseridos" value={(status?.items_inserted || 0).toLocaleString("pt-BR")} small />
      </div>

      <div className="mb-4">
        <label className="uppercase-label block mb-2">Classes CATMAT a sincronizar</label>
        <div className="flex flex-wrap gap-2">
          {CLASSES_CATMAT_SUGERIDAS.map((c) => {
            const selected = classesSelecionadas.includes(c.codigo);
            return (
              <button
                key={c.codigo}
                type="button"
                onClick={() => toggleClasse(c.codigo)}
                disabled={isRunning}
                data-testid={`classe-${c.codigo}`}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors disabled:opacity-50 ${selected ? "bg-[#F59E0B] text-[#0F172A]" : "border border-[#334155] text-[#94A3B8] hover:border-[#F59E0B]"}`}
              >
                {selected && "✓ "}{c.codigo} · {c.nome}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div>
          <label className="uppercase-label block mb-2">UF</label>
          <input data-testid="cn-uf" value={form.uf} maxLength={2}
                 onChange={(e) => setForm({ ...form, uf: e.target.value.toUpperCase() })}
                 disabled={isRunning}
                 className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
        </div>
        <div>
          <label className="uppercase-label block mb-2">De (opcional)</label>
          <input data-testid="cn-di" type="date" value={form.data_inicio}
                 onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
                 disabled={isRunning}
                 className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
        </div>
        <div>
          <label className="uppercase-label block mb-2">Até (opcional)</label>
          <input data-testid="cn-df" type="date" value={form.data_fim}
                 onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
                 disabled={isRunning}
                 className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
        </div>
        <div>
          <label className="uppercase-label block mb-2">Máx CATMATs/classe</label>
          <input data-testid="cn-max" type="number" min={1} max={300}
                 value={form.max_catmats_por_classe}
                 onChange={(e) => setForm({ ...form, max_catmats_por_classe: parseInt(e.target.value, 10) || 50 })}
                 disabled={isRunning}
                 className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
        </div>
        <button
          data-testid="trigger-sync-comprasnet"
          onClick={dispatch}
          disabled={busy || isRunning || classesSelecionadas.length === 0}
          className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Database size={14} weight="duotone" />
          {isRunning ? "Em execução…" : busy ? "Iniciando…" : "Iniciar sync"}
        </button>
      </div>

      {status?.started_at && (
        <div className="text-xs text-[#94A3B8] mt-3 space-y-0.5">
          <div>Período: {status.data_inicio || "—"} a {status.data_fim || "—"} · UF: {status.uf}</div>
          <div>Início: {new Date(status.started_at).toLocaleString("pt-BR")} {status.finished_at && `· Fim: ${new Date(status.finished_at).toLocaleString("pt-BR")}`}</div>
        </div>
      )}
    </div>
  );
}

